import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Wallet, Clock, CheckCircle2, XCircle, Info } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inr, apiError } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { ScreenTitle } from "@/components/ScreenTitle";

const statusMap = {
  pending: { hi: "लंबित", icon: Clock, cls: "bg-amber-100 text-amber-700" },
  accepted: { hi: "स्वीकृत", icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-700" },
  rejected: { hi: "अस्वीकृत", icon: XCircle, cls: "bg-red-100 text-red-700" },
};

export default function JamaScreen() {
  const { member } = useAuth();
  const [group, setGroup] = useState(null);
  const [amount, setAmount] = useState(String(member.contribution || 1000));
  const [utr, setUtr] = useState("");
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const [g, d] = await Promise.all([api.get("/group"), api.get("/deposits/me")]);
    setGroup(g.data);
    setDeposits(d.data);
  };
  useEffect(() => { load(); }, []);

  const upi = group?.upi_id || "9315341037@indie";
  const payee = group?.upi_payee || "Moneyplant";
  const upiLink = `upi://pay?pa=${upi}&pn=${encodeURIComponent(payee)}&am=${amount}&cu=INR&tn=${encodeURIComponent("Group Deposit")}`;
  const qr = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(upiLink)}`;

  const copyUpi = () => {
    navigator.clipboard?.writeText(upi);
    toast.success("UPI ID कॉपी हो गया");
  };

  const submit = async () => {
    if (!/^\d{12}$/.test(utr)) return toast.error("12 अंकों का सही UTR नंबर दर्ज करें");
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("सही राशि दर्ज करें");
    setLoading(true);
    try {
      await api.post("/deposits", { amount: amt, utr });
      toast.success("UTR जमा हुआ — एडमिन सत्यापन के बाद पुष्टि होगी");
      setUtr("");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <ScreenTitle hi="जमा करें" />

      <div className="flex items-center gap-2 -mt-1">
        <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center"><Wallet className="h-5 w-5 text-white" /></div>
        <div><p className="font-display font-bold">अपना समूह</p><p className="text-[11px] text-muted-foreground -mt-0.5">ग्रुप • सक्रिय</p></div>
      </div>

      <div className="rounded-2xl bg-white p-5 shadow-sm border border-slate-100">
        <div className="text-center">
          <Wallet className="h-7 w-7 text-primary mx-auto" />
          <p className="text-sm text-muted-foreground mt-1 font-display">जमा राशि</p>
          <div className="flex items-center justify-center gap-1 mt-1">
            <span className="text-3xl font-mono font-bold text-primary">₹</span>
            <input
              data-testid="jama-amount-input"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ""))}
              className="w-32 text-3xl font-mono font-bold text-primary text-center focus:outline-none bg-transparent"
            />
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 mt-3 text-sm">
          <span className="text-muted-foreground">समूह UPI ID:</span>
          <span className="font-mono font-semibold">{upi}</span>
          <button data-testid="copy-upi-btn" onClick={copyUpi}><Copy className="h-4 w-4 text-primary" /></button>
        </div>

        <div className="mt-4 rounded-xl bg-blue-50 p-4 flex flex-col items-center">
          <img src={qr} alt="UPI QR" className="h-44 w-44 rounded-lg bg-white p-1" data-testid="jama-qr" />
          <p className="text-xs text-muted-foreground mt-2">QR कोड स्कैन करके भुगतान करें</p>
        </div>

        <a
          href={upiLink}
          data-testid="pay-upi-btn"
          className="mt-3 flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-500 to-blue-500 text-white p-3.5 text-center font-display font-bold text-lg active:scale-[0.99] transition-transform"
        >
          <span className="h-6 w-6 rounded-full bg-white text-indigo-600 text-xs font-bold flex items-center justify-center">पे</span>
          <span className="h-6 w-6 rounded-full bg-white text-[10px] font-bold flex items-center justify-center" style={{ color: "#4285F4" }}>G</span>
          Pay via PhonePe / GPay
        </a>

        <div className="mt-4 flex items-start gap-2 rounded-xl bg-amber-50 p-3 text-xs text-amber-800">
          <Info className="h-4 w-4 shrink-0 mt-0.5" />
          भुगतान करने के बाद 12 अंकों का UTR नंबर यहाँ दर्ज करें
        </div>

        <input
          data-testid="jama-utr-input"
          value={utr}
          onChange={(e) => setUtr(e.target.value.replace(/[^\d]/g, "").slice(0, 12))}
          placeholder="12 अंकों का UTR नंबर दर्ज करें"
          inputMode="numeric"
          className="w-full h-12 mt-3 px-4 rounded-xl border border-slate-200 font-mono text-sm focus:outline-none focus:border-primary"
        />

        <Button data-testid="jama-submit-btn" onClick={submit} disabled={loading} className="w-full h-12 mt-3 text-base">
          {loading ? "जमा हो रहा है…" : "सबमिट"}
        </Button>
        <p className="text-[11px] text-muted-foreground text-center mt-2">
          UTR सबमिट करने के बाद भुगतान की पुष्टि एडमिन द्वारा होगी
        </p>
      </div>

      {/* My deposits */}
      <div>
        <h3 className="font-display font-bold text-lg mb-2">मेरे जमा / My Deposits</h3>
        <div className="space-y-2">
          {deposits.length === 0 ? (
            <p className="text-center text-muted-foreground py-4 text-sm">अभी कोई जमा नहीं</p>
          ) : (
            deposits.map((d) => {
              const s = statusMap[d.status];
              const S = s.icon;
              return (
                <div key={d.id} data-testid={`deposit-${d.id}`} className="flex items-center justify-between rounded-2xl bg-white p-3.5 shadow-sm border border-slate-100">
                  <div>
                    <p className="font-mono font-semibold">{inr(d.amount)}</p>
                    <p className="text-[11px] text-muted-foreground font-mono">UTR: {d.utr}</p>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${s.cls}`}>
                    <S className="h-3.5 w-3.5" /> {s.hi}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
